'use client'

/**
 * Mode B capture: a tool page whose generator does not yet run in-page.
 *
 * The rule this exists to satisfy: a Mode B page must never ship a bare
 * `/sign-up` link. Before 2026-08-18 every /tools page threw the visitor's
 * input away at the jump, which is why the lane converted nothing. So we take
 * the brief first, persist it across the sign-up round trip, and hand it back
 * afterwards.
 *
 * What it deliberately does NOT do: pretend the job runs here. There is no
 * fake studio and no disabled Generate button. The copy above this component
 * has to say plainly that the work happens inside Soku.
 *
 * Known gap (2026-09-22): the brief cannot be injected into the post-signup
 * chat as an opening message, because the workspace chat route
 * (`/o/[orgSlug]/chat`) accepts no prefill parameter. Until it does, the brief
 * is preserved on the page and handed to the visitor to carry in. Closing that
 * gap is what upgrades these pages.
 */

import { useCallback, useState, useSyncExternalStore } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ToolAuthDialog } from '@/components/marketing/tools/tool-auth-dialog'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { Textarea } from '@/components/ui/textarea'
import { useAuthContext } from '@/hooks/use-auth-context'

// Stable module-level references: useSyncExternalStore re-subscribes whenever
// these change identity, so they must not be recreated per render.
const subscribeNoop = () => () => {}
const serverSnapshot = () => ''

export function ModeBBriefCapture({
  pagePath,
  storageKey,
  label,
  placeholder,
  ctaLabel,
  authDescription,
}: {
  pagePath: string
  storageKey: string
  label: string
  placeholder: string
  ctaLabel: string
  authDescription: string
}) {
  const router = useRouter()
  const { isLoaded, isSignedIn } = useAuthContext()
  const [draft, setDraft] = useState<string | null>(null)
  const [authOpen, setAuthOpen] = useState(false)

  // sessionStorage is browser-only, so it is read as an external store rather
  // than in an effect: the server snapshot is empty and the client snapshot is
  // whatever survived the sign-up round trip, and React reconciles the two
  // without a hydration mismatch and without a cascading setState.
  const saved = useSyncExternalStore(subscribeNoop, () => {
    try {
      return sessionStorage.getItem(storageKey) ?? ''
    } catch {
      return ''
    }
  }, serverSnapshot)

  // The draft wins once the visitor types; before that the restored value shows.
  const brief = draft ?? saved

  const persist = useCallback(
    (value: string) => {
      setDraft(value)
      try {
        if (value.trim()) sessionStorage.setItem(storageKey, value)
        else sessionStorage.removeItem(storageKey)
      } catch {
        // Losing the draft is survivable; blocking typing is not.
      }
    },
    [storageKey],
  )

  const openAuth = useCallback(() => {
    // Same shape the closed-loop studios use: put the return path on the URL
    // first so the auth form lands the visitor back here, then open.
    router.replace(`${pagePath}?redirect_url=${encodeURIComponent(pagePath)}`, {
      scroll: false,
    })
    setAuthOpen(true)
  }, [pagePath, router])

  const hasBrief = brief.trim().length > 0

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-7">
      <label
        htmlFor={storageKey}
        className="text-foreground block text-sm font-medium"
      >
        {label}
      </label>
      <Textarea
        id={storageKey}
        value={brief}
        onChange={e => persist(e.target.value)}
        placeholder={placeholder}
        rows={5}
        className="mt-3 resize-y"
      />

      {isLoaded && isSignedIn ? (
        <div className="mt-4 space-y-3">
          <p className="text-muted-foreground text-sm">
            {hasBrief
              ? 'Your brief is saved. Open Soku and paste it into a chat — the run happens there, against your brand and your credits.'
              : 'Write your brief above, then open Soku and paste it into a chat.'}
          </p>
          <div className="flex flex-wrap gap-3">
            {hasBrief ? (
              <CopyButton
                value={brief}
                showText
                text="Copy brief"
                successText="Copied"
                variant="outline"
                size="sm"
              />
            ) : null}
            <Button asChild size="sm">
              <Link href="/">Open Soku</Link>
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <p className="text-muted-foreground text-sm">
            Create a free account and your brief comes with you — it is kept on
            this page through sign-up, so you are not retyping it afterwards.
          </p>
          <Button size="sm" onClick={openAuth}>
            {ctaLabel}
          </Button>
        </div>
      )}

      <ToolAuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        title="Create your Soku account"
        description={authDescription}
      />
    </div>
  )
}
